import * as THREE from "three"

import { escapeHtml, normalizeThingKind } from "./shared"

const PLACEMENT_KIND_ICON_DEFINITIONS = {
  sensor: {
    color: "#0d9488",
    accent: "#34d399",
    sceneScale: 2.56,
    svg: ({ color, accent }) => `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
        <circle cx="32" cy="32" r="28" fill="${accent}" fill-opacity="0.12"/>
        <circle cx="32" cy="32" r="18" stroke="${color}" stroke-width="4"/>
        <circle cx="32" cy="32" r="8" stroke="${accent}" stroke-width="4"/>
        <circle cx="32" cy="32" r="3.5" fill="${color}"/>
      </svg>
    `
  },
  wireless_access_point: {
    color: "#2563eb",
    accent: "#60a5fa",
    sceneScale: 2.56,
    svg: ({ color, accent }) => `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
        <path d="M24 39c4.5-4.5 11.5-4.5 16 0" stroke="${color}" stroke-width="4.5" stroke-linecap="round"/>
        <path d="M18 31c8-8 20-8 28 0" stroke="${color}" stroke-width="4.5" stroke-linecap="round"/>
        <path d="M12 22c11.5-11.5 28.5-11.5 40 0" stroke="${color}" stroke-width="4.5" stroke-linecap="round"/>
      </svg>
    `
  },
  label: {
    color: "#0f766e",
    accent: "#5eead4",
    sceneScale: 2.56,
    svg: ({ color, accent }) => `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
        <path d="M11 23a5 5 0 0 1 5-5h18l19 14-19 14H16a5 5 0 0 1-5-5V23Z" fill="${accent}" fill-opacity="0.18" stroke="${color}" stroke-width="4" stroke-linejoin="round"/>
        <circle cx="22" cy="32" r="3.5" fill="${color}"/>
      </svg>
    `
  },
  "360_photo": {
    color: "#c2410c",
    accent: "#fb923c",
    sceneScale: 2.8,
    svg: ({ color, accent }) => `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
        <circle cx="32" cy="32" r="27" fill="${accent}" fill-opacity="0.16"/>
        <path d="M14 33c0-8.8 8.1-16 18-16s18 7.2 18 16-8.1 16-18 16-18-7.2-18-16Z" stroke="${color}" stroke-width="4" stroke-linejoin="round"/>
        <path d="M22 33c0-8.8 4.5-16 10-16s10 7.2 10 16-4.5 16-10 16-10-7.2-10-16Z" stroke="${color}" stroke-width="3" stroke-linejoin="round"/>
        <path d="M15 33h34" stroke="${accent}" stroke-width="4" stroke-linecap="round"/>
        <circle cx="32" cy="33" r="5.5" fill="${color}"/>
        <path d="M49 20l3.5-3.5M52.5 16.5H47M52.5 16.5V22" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M15 46l-3.5 3.5M11.5 49.5H17M11.5 49.5V44" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `
  }
}

const placementIconTextureLoader = new THREE.TextureLoader()
const placementIconTextureCache = new Map()

export const placementKindIconDefinition = (kind) =>
  PLACEMENT_KIND_ICON_DEFINITIONS[normalizeThingKind(kind || "")] || PLACEMENT_KIND_ICON_DEFINITIONS.sensor

export const BUILT_IN_PLACEMENT_OBJECT_TYPES = Object.keys(PLACEMENT_KIND_ICON_DEFINITIONS)

export const placementObjectTypeLabel = (value) => {
  const normalized = normalizeThingKind(value || "")
  if (normalized === "wireless_access_point") return "Wireless AP"
  if (normalized === "360_photo") return "360 Photo"
  if (normalized === "sensor") return "Sensor"
  if (normalized === "label") return "Label"
  return normalized ? normalized.replace(/_/g, " ") : "Object"
}

const placementKindIconSvg = (kind) => {
  const definition = placementKindIconDefinition(kind)
  return definition.svg(definition).trim()
}

export const placementKindIconMarkup = (kind) => placementKindIconSvg(kind)

export const placementKindIconTexture = (kind) => {
  const normalizedKind = normalizeThingKind(kind || "") || "sensor"
  if (placementIconTextureCache.has(normalizedKind)) return placementIconTextureCache.get(normalizedKind)

  const texture = placementIconTextureLoader.load(
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(placementKindIconSvg(normalizedKind))}`
  )
  texture.colorSpace = THREE.SRGBColorSpace
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.generateMipmaps = false
  placementIconTextureCache.set(normalizedKind, texture)
  return texture
}

const renderLiveDetailItems = (items) =>
  Array.from(items || [])
    .map((item) => `
      <div class="plan-viewer-popup-attribute${item.highlight ? " is-highlighted" : ""}${item.tone ? ` tone-${escapeHtml(item.tone)}` : ""}">
        <span class="plan-viewer-popup-attribute-label">${escapeHtml(item.label || "")}</span>
        <span class="plan-viewer-popup-attribute-value">${escapeHtml(item.value || "")}</span>
      </div>
    `)
    .join("")

const gaugePercent = (value, min, max) => {
  const numericValue = Number(value)
  const numericMin = Number(min)
  const numericMax = Number(max)
  if (!Number.isFinite(numericValue) || !Number.isFinite(numericMin) || !Number.isFinite(numericMax) || numericMax <= numericMin) {
    return 0
  }

  return Math.max(0, Math.min(100, ((numericValue - numericMin) / (numericMax - numericMin)) * 100))
}

export const renderLiveDetailBlocks = (blocks, mapping) => {
  const markup = Array.from(blocks || [])
    .map((block) => {
      switch (block.type) {
      case "stats":
        return `
          <div class="plan-viewer-popup-live-block is-stats">
            <div class="plan-viewer-popup-live-grid">
              ${renderLiveDetailItems(block.items)}
            </div>
          </div>
        `
      case "highlight":
        return `
          <div class="plan-viewer-popup-live-block is-highlight">
            <div class="plan-viewer-popup-live-highlight${block.tone ? ` tone-${escapeHtml(block.tone)}` : ""}">
              <span class="plan-viewer-popup-live-highlight-label">${escapeHtml(block.label || "")}</span>
              <span class="plan-viewer-popup-live-highlight-value">${escapeHtml(block.value || "")}</span>
              ${block.caption ? `<span class="plan-viewer-popup-live-highlight-caption">${escapeHtml(block.caption)}</span>` : ""}
            </div>
          </div>
        `
      case "gauge": {
        const currentPercent = gaugePercent(block.current, block.min, block.max)
        const acceptableStart = gaugePercent(block.acceptable_min, block.min, block.max)
        const acceptableEnd = gaugePercent(block.acceptable_max, block.min, block.max)
        const acceptableWidth = Math.max(0, acceptableEnd - acceptableStart)

        return `
          <div class="plan-viewer-popup-live-block is-gauge">
            <div class="plan-viewer-popup-live-gauge${block.tone ? ` tone-${escapeHtml(block.tone)}` : ""}">
              <div class="plan-viewer-popup-live-gauge-head">
                <div class="plan-viewer-popup-live-gauge-copy">
                  <span class="plan-viewer-popup-live-gauge-title">${escapeHtml(block.title || "")}</span>
                  ${block.caption ? `<span class="plan-viewer-popup-live-gauge-caption">${escapeHtml(block.caption)}</span>` : ""}
                </div>
                <span class="plan-viewer-popup-live-gauge-value">${escapeHtml(block.value || "")}</span>
              </div>
              <div class="plan-viewer-popup-live-gauge-track">
                <span class="plan-viewer-popup-live-gauge-range" style="left:${acceptableStart}%;width:${acceptableWidth}%"></span>
                <span class="plan-viewer-popup-live-gauge-marker" style="left:${currentPercent}%"></span>
              </div>
              <div class="plan-viewer-popup-live-gauge-scale">
                <span>${escapeHtml(String(block.min ?? ""))}</span>
                <span>${escapeHtml(String(block.max ?? ""))}</span>
              </div>
              ${block.items?.length ? `<div class="plan-viewer-popup-live-grid">${renderLiveDetailItems(block.items)}</div>` : ""}
            </div>
          </div>
        `
      }
      case "section":
        if (block.collapsible) {
          return `
            <div class="plan-viewer-popup-live-block is-section">
              <details class="plan-viewer-popup-live-accordion"${block.collapsed ? "" : " open"}>
                <summary class="plan-viewer-popup-live-accordion-summary">
                  <span class="plan-viewer-popup-live-section-title">${escapeHtml(block.title || "Details")}</span>
                  <i class="fa-solid fa-chevron-down" aria-hidden="true"></i>
                </summary>
                <div class="plan-viewer-popup-live-accordion-panel">
                  <div class="plan-viewer-popup-live-grid">
                    ${renderLiveDetailItems(block.items)}
                  </div>
                </div>
              </details>
            </div>
          `
        }

        return `
          <div class="plan-viewer-popup-live-block is-section">
            <div class="plan-viewer-popup-live-section">
              ${block.title ? `<h4 class="plan-viewer-popup-live-section-title">${escapeHtml(block.title)}</h4>` : ""}
              <div class="plan-viewer-popup-live-grid">
                ${renderLiveDetailItems(block.items)}
              </div>
            </div>
          </div>
        `
      case "rows":
        return `
          <div class="plan-viewer-popup-live-block is-rows">
            ${renderLiveDetailItems(block.items)}
          </div>
        `
      default:
        return ""
      }
    })
    .join("")

  if (markup) return markup

  return `
    <div class="plan-viewer-popup-attribute">
      <span class="plan-viewer-popup-attribute-label">${escapeHtml(mapping.integration_name || "")}</span>
      <span class="plan-viewer-popup-attribute-value">No live details available.</span>
    </div>
  `
}

export const buildPlacementVisual = (placement, unitSize = 1, iconScaleMultiplier = 1, modelOffsetY = 0) => {
  const root = new THREE.Group()
  const objectType = normalizeThingKind(placement.metadata?.object_type || placement.thing.kind || "")
  const definition = placementKindIconDefinition(objectType)
  const renderPosition = placement.render_position || placement.position
  const scaledSceneSize = definition.sceneScale * iconScaleMultiplier
  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(unitSize * 0.11 * scaledSceneSize, 24),
    new THREE.MeshBasicMaterial({
      color: definition.accent,
      transparent: true,
      opacity: 0.18,
      depthWrite: false
    })
  )
  shadow.rotation.x = -Math.PI / 2
  shadow.position.y = 0.005

  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: placementKindIconTexture(objectType),
      transparent: true,
      alphaTest: 0.05,
      depthWrite: false
    })
  )
  const spriteScale = unitSize * scaledSceneSize
  sprite.center.set(0.5, 0.08)
  sprite.scale.set(spriteScale, spriteScale, 1)
  sprite.position.y = unitSize * 0.12
  sprite.renderOrder = 2

  root.add(shadow, sprite)

  root.position.set(renderPosition.x, renderPosition.y + modelOffsetY, renderPosition.z)
  root.rotation.set(placement.rotation.x, placement.rotation.y, placement.rotation.z)
  root.scale.setScalar(placement.scale || 1)
  root.userData.dtPlacementId = placement.id

  root.traverse((child) => {
    child.userData.dtPlacementId = placement.id
  })

  return root
}

export const buildNavigationPointVisual = (navigationPoint, unitSize = 1, modelOffsetY = 0) => {
  const root = new THREE.Group()
  const hoverHalo = new THREE.Mesh(
    new THREE.CircleGeometry(unitSize * 0.32, 32),
    new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: false
    })
  )
  hoverHalo.rotation.x = -Math.PI / 2
  hoverHalo.position.y = unitSize * 0.022
  hoverHalo.renderOrder = 3

  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(unitSize * 0.17, 28, 20, 0, Math.PI * 2, 0, Math.PI / 2),
    new THREE.MeshStandardMaterial({
      color: "#f59e0b",
      emissive: "#fdba74",
      emissiveIntensity: 0.45,
      metalness: 0.05,
      roughness: 0.36,
      depthWrite: false,
      depthTest: false
    })
  )
  dome.scale.y = 0.42
  dome.position.y = unitSize * 0.055
  dome.renderOrder = 4

  root.add(hoverHalo, dome)
  root.position.set(
    navigationPoint.position.x,
    navigationPoint.position.y + modelOffsetY,
    navigationPoint.position.z
  )
  root.rotation.set(
    navigationPoint.rotation.x,
    navigationPoint.rotation.y,
    navigationPoint.rotation.z
  )
  root.position.add(
    new THREE.Vector3(0, unitSize * 0.04, 0).applyEuler(root.rotation)
  )
  root.userData.dtNavigationPointId = navigationPoint.id

  root.traverse((child) => {
    child.userData.dtNavigationPointId = navigationPoint.id
    if (child.isMesh && child.material) {
      child.userData.dtNavigationBaseColor = child.material.color?.getHex() || null
      child.userData.dtNavigationBaseEmissive = child.material.emissive?.getHex() || null
    }
  })

  return root
}

export const placementVisualUnitSize = (framing) => Math.max((framing?.sphere?.radius || 1) * 0.025, 0.6)
